import { Checkbox as CheckboxPrimitive } from "@base-ui/react/checkbox";
import {
  IconBuildingStore,
  IconCheck,
  IconChevronDown,
  IconMinus,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import { Popover, PopoverPopup, PopoverTrigger } from "@/components/ui/popover";
import { Radio, RadioGroup } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useShopScope } from "@/hooks/use-shop-scope";
import { type AccountConnection, fetchAmazonAccounts } from "@/lib/amazon-api";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";

/**
 * ADP-110: the analytics shop-scope multi-select. Panel layout replicates the
 * Xnurta shop picker: quick-search + country filter on top, a select-all row
 * with a "selected only" toggle, then flat two-line rows (shop name over
 * "Seller {profileId}", country + flag on the right).
 *
 * Intentional difference vs the reference: no per-shop SP/SB/SD badge — ad
 * product availability has no per-shop data source in the accounts API.
 *
 * Multiple mode is a VIEW filter for aggregated reads and never touches the
 * operational `currentShopId`. Single mode reuses the same account metadata
 * and search surface for workflows that must target exactly one Shop.
 */

interface ScopeRow {
  shopId: string;
  name: string;
  country: string;
  profileId?: string;
  accountType?: "seller" | "vendor" | null;
}

interface ShopScopeSelectProps {
  selectionMode?: "multiple" | "single";
  value?: string;
  onValueChange?: (shopId: string) => void;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
}

/** Active Shops of the active Workspace, grouped by Amazon Ads connection. */
export function filterSwitcherConnections(
  connections: AccountConnection[],
  workspaceId: string | null,
): AccountConnection[] {
  return connections
    .map((connection) => ({
      ...connection,
      shops: connection.shops.filter(
        (shop) => shop.isActive && (!workspaceId || shop.workspaceId === workspaceId),
      ),
    }))
    .filter((connection) => connection.shops.length > 0);
}

export function marketplaceCode(marketplaceName: string | undefined): string {
  const normalized = marketplaceName?.trim();
  if (!normalized) return "AD";

  const countryCode = normalized.toUpperCase();
  if (countryCode === "GB") return "UK";
  if (/^[A-Z]{2}$/.test(countryCode)) return countryCode;

  const name = normalized.toLowerCase();
  if (name.includes("co.uk")) return "UK";
  if (name.includes(".de")) return "DE";
  if (name.includes(".fr")) return "FR";
  if (name.includes(".it")) return "IT";
  if (name.includes(".es")) return "ES";
  if (name.includes(".ca")) return "CA";
  if (name.includes(".com.mx")) return "MX";
  if (name.includes(".com.br")) return "BR";
  if (name.includes(".co.jp")) return "JP";
  if (name.includes(".com.au")) return "AU";
  return "US";
}

/** "US" → 🇺🇸 via regional indicators; UK is Amazon's alias for GB. */
export function flagEmoji(countryCode: string): string {
  const cc = countryCode === "UK" ? "GB" : countryCode;
  if (!/^[A-Z]{2}$/.test(cc)) return "";
  return String.fromCodePoint(...[...cc].map((ch) => 0x1f1e6 + (ch.charCodeAt(0) - 65)));
}

export function ShopScopeSelect({
  selectionMode = "multiple",
  value,
  onValueChange,
  disabled = false,
  ariaLabel,
  className,
}: ShopScopeSelectProps = {}) {
  const { t } = useTranslation("dashboard");
  const { scopeShopIds, workspaceShops, isAll } = useShopScope();
  const setSelectedShopIds = useAuthStore((s) => s.setSelectedShopIds);
  const user = useAuthStore((s) => s.user);
  const [search, setSearch] = useState("");
  const countryFilterRef = useRef<HTMLDivElement>(null);
  const [country, setCountry] = useState<string | null>(null);
  const [selectedOnly, setSelectedOnly] = useState(false);
  const [open, setOpen] = useState(false);
  // Xnurta parity: an empty selection also means "all shops", so the panel can
  // show "none checked" while the scope stays all — picking one shop from there
  // narrows to just that shop instead of having to untick every other one.
  const [cleared, setCleared] = useState(false);
  const accountsQuery = useQuery({
    queryKey: ["amazon", "accounts"],
    queryFn: fetchAmazonAccounts,
    staleTime: 60_000,
  });
  const single = selectionMode === "single";

  if (workspaceShops.length === 0 || (!single && workspaceShops.length === 1)) return null;

  const activeWorkspaceId = user?.activeWorkspaceId ?? null;
  const connections = filterSwitcherConnections(
    accountsQuery.data?.connections ?? [],
    activeWorkspaceId,
  );

  // Rich rows once the accounts query lands; store-backed name rows before.
  const accountRows: ScopeRow[] = connections.flatMap((connection) =>
    connection.shops.map((shop) => ({
      shopId: shop.shopId,
      name: shop.shopName,
      country: marketplaceCode(shop.marketplaceName),
      profileId: shop.profileId,
      accountType: shop.accountType,
    })),
  );
  const accountRowIds = new Set(accountRows.map((row) => row.shopId));
  const rows: ScopeRow[] = [
    ...accountRows,
    ...workspaceShops
      .filter((shop) => !accountRowIds.has(shop.shopId))
      .map((shop) => ({ shopId: shop.shopId, name: shop.shopName, country: "" })),
  ];

  const selected = new Set(single ? (value ? [value] : []) : scopeShopIds);
  const checkedIds = !single && isAll && cleared ? new Set<string>() : selected;
  const allChecked = isAll && !cleared;
  const firstName = workspaceShops.find((s) => selected.has(s.shopId))?.shopName ?? "";
  const selectedRow = single ? rows.find((row) => row.shopId === value) : undefined;
  const singleLabel = selectedRow
    ? [selectedRow.name, selectedRow.country].filter(Boolean).join(" · ")
    : firstName;
  const label = single
    ? singleLabel || t("shopScope.chooseShop")
    : isAll
      ? t("shopScope.allShops")
      : selected.size === 1
        ? firstName
        : `${firstName} +${selected.size - 1}`;
  const normalizedSearch = search.trim().toLowerCase();
  const visibleRows = rows.filter((row) => {
    if (selectedOnly && !checkedIds.has(row.shopId)) return false;
    if (country && row.country !== country) return false;
    if (!normalizedSearch) return true;
    return (
      row.name.toLowerCase().includes(normalizedSearch) ||
      (row.profileId ?? "").toLowerCase().includes(normalizedSearch)
    );
  });

  const countryCodes = [...new Set(rows.map((row) => row.country).filter(Boolean))].sort();
  const countryItems = countryCodes.map((code) => ({
    value: code,
    label: `${flagEmoji(code)} ${code}`,
  }));

  function toggle(shopId: string) {
    if (single) {
      if (shopId !== value) onValueChange?.(shopId);
      setOpen(false);
      return;
    }
    const next = new Set(checkedIds);
    if (next.has(shopId)) next.delete(shopId);
    else next.add(shopId);
    // Empty → the store treats it as "all" (the scope is never empty).
    setCleared(next.size === 0);
    setSelectedShopIds([...next]);
  }

  function toggleAll() {
    setCleared(allChecked);
    setSelectedShopIds([]);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            variant="outline"
            size="sm"
            aria-label={`${ariaLabel ?? t("shopScope.title")}: ${label}`}
            title={label}
            className={cn("w-40 justify-between", className)}
            disabled={disabled}
          />
        }
      >
        <span className="flex min-w-0 items-center gap-1.5">
          <IconBuildingStore className="size-4" aria-hidden />
          <span className="truncate">{label}</span>
        </span>
        <IconChevronDown data-icon="inline-end" />
      </PopoverTrigger>
      <PopoverPopup
        align={single ? "start" : "end"}
        sideOffset={8}
        className="w-[420px] [&>[data-slot=popover-viewport]]:p-0"
      >
        <div className="flex items-center gap-2.5 p-3">
          <InputGroup className="h-9 min-w-0 flex-1 rounded-[var(--paper-radius-control)] shadow-none before:hidden">
            <InputGroupInput
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("shopScope.quickSearch")}
              aria-label={t("shopScope.quickSearch")}
              className="text-sm"
            />
            <InputGroupAddon align="inline-start">
              <IconSearch aria-hidden />
            </InputGroupAddon>
          </InputGroup>
          <div ref={countryFilterRef} className="relative w-[120px] shrink-0">
            <Select
              items={countryItems}
              value={country}
              onValueChange={setCountry}
              disabled={disabled}
            >
              <SelectTrigger
                aria-label={t("shopScope.country")}
                className={cn("w-full", country && "pr-9 [&>svg]:hidden")}
              >
                <SelectValue placeholder={t("shopScope.country")} />
              </SelectTrigger>
              <SelectContent align="end" alignItemWithTrigger={false} className="min-w-[120px]">
                <SelectGroup>
                  {countryItems.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            {country ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                className="absolute right-2 top-1/2 -translate-y-1/2"
                aria-label={t("shopScope.clearCountry")}
                disabled={disabled}
                onClick={() => {
                  setCountry(null);
                  countryFilterRef.current
                    ?.querySelector<HTMLButtonElement>("[data-slot=select-trigger]")
                    ?.focus();
                }}
              >
                <IconX className="size-3.5" aria-hidden />
              </Button>
            ) : null}
          </div>
        </div>
        {!single ? (
          <div className="flex h-11 items-center justify-between border-b border-line px-3">
            {/* Base UI Checkbox renders a button, so a <label> can't associate with
              it — the row is a plain flex container and the text is clickable via
              the checkbox's own accessible name. */}
            <div className="flex items-center gap-2.5">
              <CheckboxPrimitive.Root
                checked={allChecked}
                indeterminate={!isAll}
                onCheckedChange={toggleAll}
                aria-label={t("shopScope.selectAll", {
                  selected: checkedIds.size,
                  total: workspaceShops.length,
                })}
                className="flex size-4.5 shrink-0 items-center justify-center rounded-[5px] border border-control-border bg-control-default text-primary-foreground outline-none ring-ring focus-visible:ring-2 data-checked:border-primary data-checked:bg-primary data-indeterminate:border-primary data-indeterminate:bg-primary"
              >
                <CheckboxPrimitive.Indicator className="grid place-content-center">
                  {allChecked ? (
                    <IconCheck className="size-3.5" stroke={3} aria-hidden />
                  ) : (
                    <IconMinus className="size-3.5" stroke={3} aria-hidden />
                  )}
                </CheckboxPrimitive.Indicator>
              </CheckboxPrimitive.Root>
              {/* Hidden from the a11y tree: the checkbox is the announced control,
                this is just a wider mouse hit area for the same action. */}
              <button
                type="button"
                tabIndex={-1}
                aria-hidden
                className="cursor-pointer text-[13px] font-medium text-ink"
                onClick={toggleAll}
              >
                {t("shopScope.selectAll", {
                  selected: checkedIds.size,
                  total: workspaceShops.length,
                })}
              </button>
            </div>
            <button
              type="button"
              aria-pressed={selectedOnly}
              onClick={() => setSelectedOnly((prev) => !prev)}
              className={cn(
                "text-[13px] font-medium text-primary hover:underline",
                selectedOnly && "underline",
              )}
            >
              {t("shopScope.selectedOnly", { count: checkedIds.size })}
            </button>
          </div>
        ) : null}
        <div
          data-testid="shop-scope-rows"
          className="paper-scrollbar max-h-[340px] overflow-y-auto"
        >
          {visibleRows.length === 0 ? (
            <p className="px-3 py-4 text-sm text-muted-foreground">{t("shopScope.noMatch")}</p>
          ) : single ? (
            // Base UI checks a radio when arrow keys move focus onto it; switching Shops
            // there would close the popover mid-navigation. The group stays pinned to the
            // committed value and rows commit only on click, Space, or Enter.
            <RadioGroup className="gap-0" value={value ?? null}>
              {visibleRows.map((row) => (
                <ScopeShopRow
                  key={row.shopId}
                  row={row}
                  checked={selected.has(row.shopId)}
                  onToggle={() => toggle(row.shopId)}
                  selectionMode="single"
                  disabled={disabled}
                />
              ))}
            </RadioGroup>
          ) : (
            visibleRows.map((row) => (
              <ScopeShopRow
                key={row.shopId}
                row={row}
                checked={checkedIds.has(row.shopId)}
                onToggle={() => toggle(row.shopId)}
                selectionMode="multiple"
                disabled={disabled}
              />
            ))
          )}
        </div>
        <div
          className={cn(
            "flex items-center gap-3 border-t border-line px-3 py-2 text-[11px] text-muted-foreground",
            single ? "justify-end" : "justify-between",
          )}
        >
          {!single ? <span>{t("shopScope.emptyRule")}</span> : null}
          <Link to="/settings" className="shrink-0 font-medium text-ink hover:underline">
            {t("shopScope.manageConnections")} →
          </Link>
        </div>
      </PopoverPopup>
    </Popover>
  );
}

/**
 * Single-Shop picker bound to the operational `currentShopId`, for pages that read and write
 * one ambient Shop and have no resource of their own to follow.
 */
export function CurrentShopSelect({
  disabled = false,
  disabledReason,
}: {
  disabled?: boolean;
  disabledReason?: string;
}) {
  const currentShopId = useAuthStore((s) => s.currentShopId);
  const setCurrentShop = useAuthStore((s) => s.setCurrentShop);
  const queryClient = useQueryClient();
  return (
    <span title={disabled ? disabledReason : undefined}>
      <ShopScopeSelect
        selectionMode="single"
        value={currentShopId ?? undefined}
        disabled={disabled}
        onValueChange={(shopId) => {
          setCurrentShop(shopId);
          void queryClient.invalidateQueries();
        }}
      />
    </span>
  );
}

function ScopeShopRow({
  row,
  checked,
  onToggle,
  selectionMode,
  disabled,
}: {
  row: ScopeRow;
  checked: boolean;
  onToggle: () => void;
  selectionMode: "multiple" | "single";
  disabled: boolean;
}) {
  const typeLabel = row.accountType === "vendor" ? "Vendor" : row.accountType ? "Seller" : "";
  return (
    // Base UI Checkbox renders a button (no <label> association); the whole row
    // toggles via onClick while the checkbox stops propagation to avoid a
    // double toggle.
    // biome-ignore lint/a11y/noStaticElementInteractions: the inner checkbox is the accessible control; the row is a convenience hit area.
    // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard users operate the focusable checkbox itself.
    <div
      onClick={disabled ? undefined : onToggle}
      className={cn(
        "flex cursor-pointer items-center gap-2.5 border-b border-line/60 px-3 py-2.5 last:border-b-0 hover:bg-subtle/70",
        checked && "bg-subtle/50",
        disabled && "cursor-default opacity-60",
      )}
    >
      {selectionMode === "single" ? (
        <Radio
          value={row.shopId}
          disabled={disabled}
          // Base UI routes Space and Enter on the radio through onClick as well.
          onClick={(event) => {
            event.stopPropagation();
            if (!disabled) onToggle();
          }}
          aria-label={row.name}
        />
      ) : (
        <Checkbox
          checked={checked}
          disabled={disabled}
          onCheckedChange={onToggle}
          onClick={(event) => event.stopPropagation()}
          aria-label={row.name}
        />
      )}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-[13px] font-semibold leading-[18px] text-ink">
          {row.name}
        </span>
        {row.profileId ? (
          <span className="truncate text-[12px] leading-4">
            {typeLabel ? <span className="text-primary">{typeLabel} </span> : null}
            <span className="text-muted-foreground">{row.profileId}</span>
          </span>
        ) : null}
      </span>
      {row.country ? (
        <span className="flex shrink-0 items-center gap-1.5 text-[13px] font-medium text-muted-foreground">
          {row.country}
          <span aria-hidden>{flagEmoji(row.country)}</span>
        </span>
      ) : null}
    </div>
  );
}
